<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">User Management</h1>
            <p class="text-orange-100 text-lg">Manage all users in the system</p>
          </div>
          <div class="flex space-x-3">
            <button
              @click="openAddUserModal"
              class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200"
            >
              <svg class="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              Add User
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="bg-white shadow-lg rounded-xl border border-gray-200 p-6 mb-8">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name, email, or phone..."
              class="w-full border-2 border-blue-300 rounded-lg py-3 pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
            />
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <button
              v-if="searchQuery"
              @click="clearSearch"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Clear search"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Role</label>
          <div class="relative">
            <select v-model="selectedRole" class="w-full border-2 border-blue-300 rounded-lg py-3 pl-10 pr-8 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none">
              <option value="">All Roles</option>
              <option v-for="role in userRolesStore.activeRoles" :key="role.id" :value="role.name">
                {{ role.display_name }}
              </option>
            </select>
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <div class="relative">
            <select v-model="selectedStatus" class="w-full border-2 border-blue-300 rounded-lg py-3 pl-10 pr-8 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Users Table -->
    <div class="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
              <th class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="user in displayedUsers" :key="user.id" class="hover:bg-gray-50 transition-all duration-200">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10">
                    <div class="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center shadow-lg">
                      <span class="text-sm font-medium text-white">{{ user.name.charAt(0) }}</span>
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-gray-900">{{ user.name }}</div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ user.email }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ user.phone }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="getRoleClass(user.role)" class="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full">
                  {{ formatRole(user.role) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center space-x-2">
                  <span :class="getEffectiveStatus(user) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'" class="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ getEffectiveStatus(user) ? 'Inactive' : 'Active' }}
                  </span>
                  <!-- Show indicator if deactivated due to login attempts -->
                  <span v-if="!user.active && getEffectiveStatus(user)" class="text-xs text-gray-500">
                    (Manual)
                  </span>
                  <span v-if="user.active && getEffectiveStatus(user)" class="text-xs text-orange-600">
                    (Login Limit)
                  </span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ formatDate(user.created_at) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button @click="editUser(user)" class="text-orange-600 hover:text-orange-900 mr-3 transition-colors duration-200">Edit</button>
                <button 
                  @click="toggleUserStatus(user)" 
                  :disabled="!canDeactivateUser(user)"
                  :class="[
                    getEffectiveStatus(user) ? 'text-green-600 hover:text-green-900' : 'text-red-600 hover:text-red-900',
                    !canDeactivateUser(user) ? 'opacity-50 cursor-not-allowed' : ''
                  ]" 
                  class="transition-colors duration-200"
                  :title="!canDeactivateUser(user) ? `${user.role.charAt(0).toUpperCase() + user.role.slice(1)} users cannot be deactivated` : ''"
                >
                  {{ getEffectiveStatus(user) ? 'Activate' : 'Deactivate' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="displayedUsers.length === 0 && !loading" class="text-center py-12">
      <div class="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
        <svg class="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
        </svg>
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No users found</h3>
      <p class="text-gray-500">Try adjusting your filters or add a new user.</p>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-xl">
      <div class="flex-1 flex justify-between sm:hidden">
        <button
          @click="prevPage"
          :disabled="currentPage === 1"
          class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          Previous
        </button>
        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          Next
        </button>
      </div>
      <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            Showing
            <span class="font-medium">{{ (currentPage - 1) * pageSize + 1 }}</span>
            to
            <span class="font-medium">{{ Math.min(currentPage * pageSize, totalUsers) }}</span>
            of
            <span class="font-medium">{{ totalUsers }}</span>
            results
          </p>
        </div>
        <div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span class="sr-only">Previous</span>
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
            
            <template v-for="page in visiblePages" :key="page">
              <button
                v-if="page !== '...'"
                @click="goToPage(page)"
                :class="[
                  page === currentPage
                    ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors duration-200'
                ]"
              >
                {{ page }}
              </button>
              <span v-else class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                ...
              </span>
            </template>
            
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span class="sr-only">Next</span>
              <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>

    <!-- Add/Edit User Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div class="relative bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-md">
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-xl font-bold text-gray-900">{{ isEditing ? 'Edit User' : 'Add New User' }}</h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 transition-colors duration-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <form @submit.prevent="saveUser" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Name *</label>
              <input
                v-model="formData.name"
                type="text"
                required
                class="w-full border-2 border-blue-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.name }"
              />
              <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                v-model="formData.email"
                type="email"
                class="w-full border-2 border-blue-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.email }"
              />
              <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                v-model="formData.phone"
                type="tel"
                class="w-full border-2 border-blue-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.phone }"
              />
              <p v-if="errors.phone" class="mt-1 text-sm text-red-600">{{ errors.phone }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Role *
                <span v-if="isEditing && !canChangeRole(currentUser)" class="text-xs text-gray-500 ml-2">
                  (Cannot be changed)
                </span>
              </label>
              <select
                v-model="formData.role"
                :disabled="isEditing && !canChangeRole(currentUser)"
                required
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 
                  'border-red-500': errors.role,
                  'bg-gray-100 cursor-not-allowed text-gray-600': isEditing && !canChangeRole(currentUser)
                }"
              >
                <option value="">Select Role</option>
                <option v-for="role in availableRoles" :key="role.id" :value="role.name">
                  {{ role.display_name }}
                </option>
              </select>
              <p v-if="errors.role" class="mt-1 text-sm text-red-600">{{ errors.role }}</p>
              <!-- Role restriction messages -->
              <div v-if="!isEditing" class="mt-2 space-y-1">
                <p v-if="adminUserExists" class="text-sm text-amber-600">
                  ⚠️ Admin user already exists. Only one admin user is allowed.
                </p>
                <p v-if="workshopUserExists" class="text-sm text-amber-600">
                  ⚠️ Workshop user already exists. Please edit the existing user if modifications are needed.
                </p>
              </div>
              <!-- Admin role change restriction message -->
              <div v-if="isEditing && !canChangeRole(currentUser)" class="mt-2">
                <p class="text-sm text-amber-600">
                  ⚠️ Admin users cannot change their own role to prevent system lockout
                </p>
                <p class="text-sm text-gray-600 mt-1">
                  You can edit: Name, Email, Phone, Password. Role cannot be changed to prevent system lockout.
                </p>
              </div>
            </div>

            <!-- Session Management Fields (only for customers) -->
            <div v-if="formData.role === 'customer' || (isEditing && currentUser?.role === 'customer')" class="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 class="text-sm font-medium text-gray-900 mb-3">Session Management</h4>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Max Login Attempts</label>
                  <input
                    v-model="formData.max_attempts"
                    type="number"
                    min="1"
                    max="100"
                    class="w-full border-2 border-blue-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    :class="{ 'border-red-500': errors.max_attempts }"
                  />
                  <p v-if="errors.max_attempts" class="mt-1 text-sm text-red-600">{{ errors.max_attempts }}</p>
                  <p class="mt-1 text-xs text-gray-500">Default: 5 attempts</p>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Session Timeout</label>
                  <input
                    v-model="formData.session_timeout_minutes"
                    type="number"
                    min="5"
                    max="1440"
                    placeholder="30"
                    class="w-full border-2 border-blue-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    :class="{ 'border-red-500': errors.session_timeout_minutes }"
                  />
                  <p v-if="errors.session_timeout_minutes" class="mt-1 text-sm text-red-600">{{ errors.session_timeout_minutes }}</p>
                  <p class="mt-1 text-xs text-gray-500">Default: 30 minutes (5-1440 min range)</p>
                </div>
              </div>
              
              <div v-if="isEditing && currentUser && getEffectiveStatus(currentUser)" class="text-xs text-gray-600">
                <!-- Warning for deactivated users -->
                <div class="mt-3 p-2 bg-orange-50 border border-orange-200 rounded">
                  <p class="text-orange-800 font-medium">⚠️ User is currently deactivated</p>
                  <p class="text-orange-700 mt-1">
                    <span v-if="currentUser.active && currentUser.login_attempts >= currentUser.max_attempts && currentUser.role !== 'admin' && currentUser.role !== 'staff'">
                      Deactivated due to exceeding login attempts ({{ currentUser.login_attempts }}/{{ currentUser.max_attempts }})
                    </span>
                    <span v-else>
                      Manually deactivated
                    </span>
                  </p>
                  <p class="text-orange-600 mt-1 text-xs">
                    💡 Updating max attempts or session timeout will reset login attempts to 0
                  </p>
                </div>
              </div>
            </div>

            <!-- Password field for both create and edit -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ isEditing ? 'New Password' : 'Password' }} <span v-if="!isEditing" class="text-red-500">*</span>
                <span v-if="isEditing" class="text-xs text-gray-500">(leave blank to keep current password)</span>
              </label>
              <div class="relative">
                <input
                  v-model="formData.password"
                  :type="showPassword ? 'text' : 'password'"
                  :required="!isEditing"
                  class="w-full border-2 border-blue-300 rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  :class="{ 'border-red-500': errors.password }"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Toggle password visibility"
                >
                  <svg v-if="showPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.758 7.758M12 12l2.122 2.122m-2.122-2.122L7.758 7.758m6.364 6.364L12 12m2.122 2.122l2.122 2.122" />
                  </svg>
                  <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
              <p v-if="errors.password" class="mt-1 text-sm text-red-600">{{ errors.password }}</p>
              <p v-if="!isEditing" class="mt-1 text-xs text-gray-500">Password is required for user creation</p>
            </div>

            <div class="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="saving"
                class="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
              >
                {{ saving ? 'Saving...' : (isEditing ? 'Update User' : 'Add User') }}
              </button>
            </div>
            
            <!-- Inline Error Message -->
            <div v-if="formError" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div class="flex items-start justify-between">
                <div class="flex items-start">
                  <svg class="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p class="text-sm font-medium text-red-800">{{ formError.title }}</p>
                    <p v-if="formError.message" class="text-sm text-red-700 mt-1">{{ formError.message }}</p>
                  </div>
                </div>
                <button @click="formError = null" class="text-red-400 hover:text-red-600">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- Inline Success Message -->
            <div v-if="formSuccess" class="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div class="flex items-start justify-between">
                <div class="flex items-start">
                  <svg class="h-5 w-5 text-green-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p class="text-sm font-medium text-green-800">{{ formSuccess.title }}</p>
                    <p v-if="formSuccess.message" class="text-sm text-green-700 mt-1">{{ formSuccess.message }}</p>
                  </div>
                </div>
                <button @click="formSuccess = null" class="text-green-400 hover:text-green-600">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { adminAPI } from '../../services/api'
import { useNotificationStore } from '../../stores/notification'
import { useUserRolesStore } from '../../stores/userRoles'
import { useUserStore } from '../../stores/user'

export default {
  name: 'AdminUsers',
  setup() {
    const selectedRole = ref('')
    const selectedStatus = ref('')
    const searchQuery = ref('')
    const loading = ref(false)
    const saving = ref(false)
    const showModal = ref(false)
    const showPassword = ref(false)
    const isEditing = ref(false)
    const editingUserId = ref(null)
    const currentUser = ref(null)
    
    // Pagination state
    const currentPage = ref(1)
    const totalPages = ref(1)
    const totalUsers = ref(0)
    const pageSize = ref(20)
    
    // Stores
    const notificationStore = useNotificationStore()
    const userRolesStore = useUserRolesStore()
    const userStore = useUserStore()

    // Form data
    const formData = ref({
      name: '',
      email: '',
      phone: '',
      role: '',
      password: '',
      max_attempts: 5,
      session_timeout_minutes: 30
    })

    // Form errors
    const errors = ref({})
    
    // Form error for inline display
    const formError = ref(null)
    
    // Form success for inline display
    const formSuccess = ref(null)

    // Users data
    const users = ref([])

    // Calculate effective status considering login attempts
    const getEffectiveStatus = (user) => {
      // Check if user is deactivated due to exceeding login attempts
      const isAnnzzUser = user.name && user.name.toLowerCase().includes('annzz')
      const isAdminOrStaff = user.role === 'admin' || user.role === 'staff'
      const isDeactivatedByAttempts = !isAnnzzUser && !isAdminOrStaff && user.login_attempts >= user.max_attempts
      
      // User is effectively inactive if either:
      // 1. Database active field is false, OR
      // 2. User has exceeded login attempts (except for ANNZZ users, admin, and staff)
      return !user.active || isDeactivatedByAttempts
    }

    // Display users directly from server (no client-side filtering needed)
    const displayedUsers = computed(() => users.value)

    const formatRole = (role) => {
      return userRolesStore.getRoleDisplayName(role)
    }

    const getRoleClass = (role) => {
      return userRolesStore.getRoleColorClass(role)
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-IN')
    }

    // Check if admin/workshop users already exist. Fetched from the server
    // since users.value only holds the current page
    const adminUserExists = ref(false)
    const workshopUserExists = ref(false)

    const checkRoleLimits = async () => {
      try {
        const [admins, workshops] = await Promise.all([
          adminAPI.getUsers({ role: 'admin', active: true, limit: 1 }),
          adminAPI.getUsers({ role: 'workshop', active: true, limit: 1 })
        ])
        adminUserExists.value = admins.data.pagination.total > 0
        workshopUserExists.value = workshops.data.pagination.total > 0
      } catch (error) {
        console.error('Failed to check role limits:', error)
      }
    }

    // Get available roles for user creation (exclude admin if exists, workshop if exists)
    // When editing, always include the current user's role so it can be displayed
    const availableRoles = computed(() => {
      const filteredRoles = userRolesStore.activeRoles.filter(role => {
        if (role.name === 'admin' && adminUserExists.value) return false
        if (role.name === 'workshop' && workshopUserExists.value) return false
        return true
      })

      if (isEditing.value && currentUser.value?.role) {
        // Customers and company users cannot be converted into each other
        const isCustomer = currentUser.value.role === 'customer'
        for (let i = filteredRoles.length - 1; i >= 0; i--) {
          if ((filteredRoles[i].name === 'customer') !== isCustomer) filteredRoles.splice(i, 1)
        }

        // If current user has a role that's filtered out, add it back
        const currentUserRole = userRolesStore.activeRoles.find(role => role.name === currentUser.value.role)
        if (currentUserRole && !filteredRoles.some(role => role.name === currentUser.value.role)) {
          filteredRoles.push(currentUserRole)
        }
      }

      return filteredRoles
    })

    // Check if user can be deactivated
    const canDeactivateUser = (user) => {
      return user.role !== 'admin' && user.role !== 'workshop'
    }

    // Check if role can be changed (admin cannot change their own role)
    const canChangeRole = (user) => {
      return user.role !== 'admin'
    }

    const clearSearch = () => {
      searchQuery.value = ''
    }

    const openAddUserModal = () => {
      isEditing.value = false
      editingUserId.value = null
      resetForm()
      checkRoleLimits()
      showModal.value = true
    }

    const editUser = (user) => {
      isEditing.value = true
      editingUserId.value = user.id
      currentUser.value = user
      
      formData.value = {
        name: user.name,
        email: user.email || '',
        phone: user.phone || '',
        role: user.role,
        password: '',
        max_attempts: user.max_attempts || 5,
        session_timeout_minutes: user.session_timeout_minutes || 30
      }
      checkRoleLimits()
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      resetForm()
    }

    const resetForm = () => {
      formData.value = {
        name: '',
        email: '',
        phone: '',
        role: '',
        password: '',
        max_attempts: 5,
        session_timeout_minutes: 30
      }
      errors.value = {}
      formError.value = null
      formSuccess.value = null
      currentUser.value = null
    }

    const validateForm = () => {
      errors.value = {}

      if (!formData.value.name.trim()) {
        errors.value.name = 'Name is required'
      }

      if (formData.value.email && !isValidEmail(formData.value.email)) {
        errors.value.email = 'Please enter a valid email'
      }

      if (formData.value.phone && !isValidPhone(formData.value.phone)) {
        errors.value.phone = 'Please enter a valid phone number'
      }

      if (!formData.value.role) {
        errors.value.role = 'Role is required'
      }

      // Password validation
      if (!isEditing.value) {
        // For new users, password is required
        if (!formData.value.password || formData.value.password.trim() === '') {
          errors.value.password = 'Password is required'
        } else if (formData.value.password.length < 6) {
          errors.value.password = 'Password must be at least 6 characters'
        }
      } else {
        // For editing, password is optional but must be at least 6 characters if provided
        if (formData.value.password && formData.value.password.length < 6) {
          errors.value.password = 'Password must be at least 6 characters'
        }
      }

      // Validate session management fields for customers
      if (formData.value.role === 'customer') {
        if (formData.value.max_attempts && (formData.value.max_attempts < 1 || formData.value.max_attempts > 100)) {
          errors.value.max_attempts = 'Max attempts must be between 1 and 100'
        }
        
        if (formData.value.session_timeout_minutes && (formData.value.session_timeout_minutes < 5 || formData.value.session_timeout_minutes > 1440)) {
          errors.value.session_timeout_minutes = 'Session timeout must be between 5 and 1440 minutes'
        }
      }

      return Object.keys(errors.value).length === 0
    }

    const isValidEmail = (email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    }

    const isValidPhone = (phone) => {
      const phoneRegex = /^\+?[1-9]\d{1,14}$/
      return phoneRegex.test(phone)
    }

    const saveUser = async () => {
      if (!validateForm()) return

      saving.value = true
      try {
        const userData = { ...formData.value }
        
        // Remove empty fields
        if (!userData.email) delete userData.email
        if (!userData.phone) delete userData.phone
        // Only remove password if it's empty - we want to send it if user provided a new password
        if (!userData.password) delete userData.password

        if (isEditing.value) {
          // Update existing user
          await adminAPI.updateUser(editingUserId.value, userData)
          
          // If current user is editing their own profile, update the user store
          if (userStore.user && userStore.user.id === editingUserId.value) {
            userStore.updateUser({
              name: userData.name,
              phone: userData.phone,
              email: userData.email
            })
          }
          
          // Check if this was reactivating a deactivated user
          const wasDeactivated = currentUser.value && getEffectiveStatus(currentUser.value)
          const updatedLimits = userData.max_attempts || userData.session_timeout_minutes
          
          if (wasDeactivated && updatedLimits) {
            formSuccess.value = {
              title: 'User Reactivated',
              message: 'User has been reactivated and login attempts have been reset to 0'
            }
          } else {
            formSuccess.value = {
              title: 'User Updated',
              message: 'User has been updated successfully'
            }
          }
        } else {
          // Create new user
          await adminAPI.createUser(userData)
          formSuccess.value = {
            title: 'User Created',
            message: 'User has been created successfully'
          }
        }

        // Refresh users list
        await loadUsers(currentPage.value)
        
        // Close modal after a short delay to show success message
        setTimeout(() => {
          closeModal()
        }, 1500)
      } catch (error) {
        console.error('Error saving user:', error)
        // Handle specific error types
        if (error.response?.status === 409) {
          const errorData = error.response.data
          
          if (errorData.error === 'DuplicateEmail') {
            errors.value.email = 'User with this email already exists'
            formError.value = {
              title: 'Duplicate Email',
              message: 'User with this email already exists'
            }
          } else if (errorData.error === 'DuplicatePhone') {
            errors.value.phone = 'User with this phone already exists'
            formError.value = {
              title: 'Duplicate Phone',
              message: 'User with this phone already exists'
            }
          } else if (errorData.error === 'RoleLimitExceeded') {
            formError.value = {
              title: 'Role Limit Exceeded',
              message: errorData.message
            }
          } else {
            formError.value = {
              title: 'Error',
              message: errorData.message || 'Failed to save user. Please try again.'
            }
          }
        } else {
          formError.value = {
            title: 'Error',
            message: 'Failed to save user. Please try again.'
          }
        }
      } finally {
        saving.value = false
      }
    }

    const toggleUserStatus = async (user) => {
      // Prevent deactivation of admin and workshop users
      if (!canDeactivateUser(user)) {
        notificationStore.showWarning('Action Not Allowed', `${user.role.charAt(0).toUpperCase() + user.role.slice(1)} users cannot be deactivated`)
        return
      }

      try {
        const effectiveStatus = getEffectiveStatus(user)
        
        if (effectiveStatus) {
          // User is effectively inactive, so open edit form to reactivate
          // Check if user was deactivated due to login attempts
          const isAnnzzUser = user.name && user.name.toLowerCase().includes('annzz')
          const isAdminOrStaff = user.role === 'admin' || user.role === 'staff'
          const isDeactivatedByAttempts = !isAnnzzUser && !isAdminOrStaff && user.login_attempts >= user.max_attempts
          
          if (isDeactivatedByAttempts) {
            // Open edit form for users deactivated due to login attempts
            editUser(user)
            notificationStore.showInfo('Edit User', 'Please review and update the user settings to reactivate them.')
          } else {
            // For manually deactivated users, just toggle active status
            await adminAPI.updateUser(user.id, { active: true })
            notificationStore.showSuccess('User Activated', 'User has been activated successfully')
            await loadUsers(currentPage.value)
          }
        } else {
          // User is effectively active, so deactivate them
          await adminAPI.updateUser(user.id, { active: false })
          notificationStore.showSuccess('User Deactivated', 'User has been deactivated successfully')
          await loadUsers(currentPage.value)
        }
      } catch (error) {
        console.error('Error toggling user status:', error)
        notificationStore.showError('Error', 'Failed to update user status. Please try again.')
      }
    }

    // Incremented per request so a slow, older response can't overwrite a newer one
    let loadUsersRequestId = 0

    const loadUsers = async (page = 1, resetFilters = false) => {
      const requestId = ++loadUsersRequestId
      try {
        loading.value = true

        // Reset to page 1 if filters changed
        if (resetFilters) {
          currentPage.value = 1
          page = 1
        }

        const response = await adminAPI.getUsers({
          page: page,
          limit: pageSize.value,
          role: selectedRole.value || undefined,
          active: selectedStatus.value === 'active' ? true : selectedStatus.value === 'inactive' ? false : undefined,
          search: searchQuery.value || undefined
        })
        if (requestId !== loadUsersRequestId) return

        // Page no longer exists (e.g. last user on the last page was filtered out)
        const { pages, total } = response.data.pagination
        if (page > 1 && page > pages) {
          return loadUsers(Math.max(pages, 1))
        }

        users.value = response.data.users || []
        totalPages.value = pages
        totalUsers.value = total
        currentPage.value = page

      } catch (error) {
        if (requestId !== loadUsersRequestId) return
        console.error('Error loading users:', error)
        notificationStore.showError('Error', 'Failed to load users. Please try again.')
        users.value = []
      } finally {
        if (requestId === loadUsersRequestId) loading.value = false
      }
    }

    // Watch for form data changes to clear errors
    watch(formData, () => {
      if (formError.value) {
        formError.value = null
      }
    }, { deep: true })

    // Pagination methods
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        loadUsers(page)
      }
    }

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        goToPage(currentPage.value + 1)
      }
    }

    const prevPage = () => {
      if (currentPage.value > 1) {
        goToPage(currentPage.value - 1)
      }
    }

    // Computed property for visible pagination pages
    const visiblePages = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) pages.push(i)
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) pages.push(i)
          pages.push('...')
          pages.push(total)
        }
      }
      
      return pages
    })

    // Watch for filter changes to trigger server requests
    watch([selectedRole, selectedStatus], () => {
      loadUsers(1, true) // Reset to page 1 with new filters
    })

    // Debounce search so we don't hit the server on every keystroke
    let searchTimer = null
    watch(searchQuery, () => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => loadUsers(1, true), 300)
    })

    onMounted(async () => {
      await userRolesStore.fetchRoles()
      loadUsers(1) // Load first page
    })

    return {
      selectedRole,
      selectedStatus,
      loading,
      saving,
      showModal,
      showPassword,
      isEditing,
      formData,
      errors,
      formError,
      formSuccess,
      users,
      displayedUsers,
      currentUser,
      formatRole,
      getRoleClass,
      formatDate,
      getEffectiveStatus,
      searchQuery,
      clearSearch,
      openAddUserModal,
      editUser,
      closeModal,
      saveUser,
      toggleUserStatus,
      userRolesStore,
      adminUserExists,
      workshopUserExists,
      availableRoles,
      canDeactivateUser,
      canChangeRole,
      userStore,
      // Pagination
      currentPage,
      totalPages,
      totalUsers,
      pageSize,
      goToPage,
      nextPage,
      prevPage,
      visiblePages
    }
  }
}
</script> 